import React, { useState, useRef } from 'react';
import { Drawer, Button, Tabs } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { API_ORIGIN } from '../config';
import DOTheme from './directOrders/doTheme';
import ApprovalTrack from './directOrders/ApprovalTrack';
import StatusBadge from './directOrders/StatusBadge';
import AttachmentsViewer from './directOrders/AttachmentsViewer';
import { formatDate, formatEuro, getInitiative, getReference, getStatusLabel } from '../utils/directOrderStatus';

const printStyles = `
  body { font-family: Arial, sans-serif; line-height: 1.5; margin: 24px; color: #101828; }
  .do-doc__title { font-size: 22px; margin: 0 0 4px; }
  .do-doc__status { margin: 0 0 20px; color: #344054; }
  .do-kv-group { margin-top: 20px; }
  .do-kv-group__title { font-size: 14px; margin: 0 0 4px; }
  .do-kv { width: 100%; border-collapse: collapse; table-layout: fixed; }
  .do-kv th, .do-kv td { border: 1px solid #ddd; padding: 8px 10px; text-align: left; vertical-align: top; overflow-wrap: anywhere; font-size: 13px; }
  .do-kv th { width: 34%; background: #f2f2f2; font-weight: 600; }
  .do-kv td { white-space: pre-wrap; }
  @media print { body { margin: 0; } }
`;

// Field groups shown in the detail view and the printout. Labels follow the
// wording on the paper Direct Order form.
const buildGroups = (order) => [
  {
    title: 'Request',
    rows: [
      ['Date', formatDate(order.doDate)],
      ['Entity and department', order.doEntityName],
      ['Initiative', order.doInitiativeName],
      [
        'Line vote',
        order.departmentCode && order.LineVote && order.lineVoteFestival
          ? `${order.departmentCode}/${order.LineVote}/${order.lineVoteFestival}`
          : '',
      ],
      ['Additional line votes', (order.additionalLineVotes || []).map((lv) => lv.label).join('\n')],
      ['Officer raising the request', order.doOfficerNameFull],
      ['Perm Sec / Chairman approving', order.doChairmanName],
    ],
  },
  {
    title: 'Justification',
    rows: [
      ['Procurement policy note 32, section 1 (a to e)', order.doJustificationName],
      ['Detailed justification', order.doDetailedJustification],
    ],
  },
  {
    title: 'Supplier and items',
    rows: [
      ['Supplier', order.doSupplier],
      ['Name and brief description', order.doNameBriefDesc],
      ['Breakdown of works, supplies or services', order.doBreakdown],
      ['Time frame of contract', order.doTimeframe],
      ['Cost per item or rate per hour (excl. VAT)', order.doCostPerItem],
      ['Total cost (excl. VAT)', order.doTotalCost !== undefined && order.doTotalCost !== null && order.doTotalCost !== '' ? formatEuro(order.doTotalCost) : ''],
    ],
  },
  {
    title: 'Procurement and funding',
    rows: [
      ['Call for quotation or expression of interest', order.doCallForQuiteOrInterest],
      ['Terms of payment', order.doTermsOfPayment],
      ['Reference and date of winning bid, contract or offer', order.doRefNoAndDateOfWiningBidEtc],
      ['Availability and source of funds', order.doFundsAvailability],
      ['Two-year history with this supplier', order.doBriefHistory],
    ],
  },
];

const escapeHtml = (value) =>
  String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const DirectOrderViewModal = ({ visible, onClose, directOrder }) => {
  const [activeTab, setActiveTab] = useState('details');
  const printRef = useRef();

  if (!directOrder) {
    return null;
  }

  const reference = getReference(directOrder);
  const pdfUrl = directOrder.doId ? `${API_ORIGIN}/api/direct-order/${directOrder.doId}/pdf` : null;
  const groups = buildGroups(directOrder);

  const handlePrint = () => {
    try {
      const printContent = printRef.current;
      if (!printContent) {
        return;
      }

      const winPrint = window.open('', '', 'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0');

      if (!winPrint) {
        alert('Popup blocked! Please allow popups for this site to enable printing.');
        return;
      }

      winPrint.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Direct Order - ${escapeHtml(reference)}</title>
          <style>${printStyles}</style>
        </head>
        <body>
          <h1 class="do-doc__title">Request for Direct Order ${escapeHtml(reference)}</h1>
          <p class="do-doc__status">${escapeHtml(getStatusLabel(directOrder))}</p>
          ${printContent.innerHTML}
        </body>
        </html>
      `);

      winPrint.document.close();
      winPrint.focus();

      // Small delay so the content is laid out before printing
      setTimeout(() => {
        winPrint.print();
        winPrint.close();
      }, 250);
    } catch (error) {
      alert('An error occurred while trying to print. Please try again.');
    }
  };

  const tabItems = [
    {
      key: 'details',
      label: 'Details',
      children: (
        <div ref={printRef}>
          {groups.map((group) => (
            <section className="do-kv-group" key={group.title}>
              <h3 className="do-kv-group__title">{group.title}</h3>
              <table className="do-kv">
                <tbody>
                  {group.rows.map(([label, value]) => (
                    <tr key={label}>
                      <th scope="row">{label}</th>
                      <td className={value ? '' : 'is-empty'}>{value || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      ),
    },
    ...(pdfUrl
      ? [
          {
            key: 'attachments',
            label: 'Attachments',
            children: (
              <AttachmentsViewer doId={directOrder.doId} attachments={directOrder.doAttachments} title={`Attachments for ${reference}`} />
            ),
          },
        ]
      : []),
  ];

  return (
    <DOTheme>
      <Drawer
        open={visible}
        onClose={onClose}
        width={760}
        rootClassName="do-root do-scope"
        title={
          <span style={{ fontWeight: 600 }}>
            Direct order <span className="do-ref">{reference}</span>
          </span>
        }
        extra={
          <Button icon={<PrinterOutlined />} onClick={handlePrint}>
            Print
          </Button>
        }
        destroyOnClose
      >
        <div style={{ marginBottom: 4 }}>
          <StatusBadge order={directOrder} />
        </div>
        <div className="do-fact__value" style={{ fontSize: 22, lineHeight: '28px', letterSpacing: '-0.01em' }}>
          {directOrder.doSupplier || 'Unknown supplier'}
        </div>
        <div className="do-detail__summary">
          <div>
            <span className="do-fact__label">Total (excl. VAT)</span>
            <span className="do-fact__value do-num">{formatEuro(directOrder.doTotalCost)}</span>
          </div>
          <div>
            <span className="do-fact__label">Initiative</span>
            <span className="do-fact__value">{getInitiative(directOrder)}</span>
          </div>
          <div>
            <span className="do-fact__label">Raised</span>
            <span className="do-fact__value">{formatDate(directOrder.doDate)}</span>
          </div>
        </div>
        <div className="do-detail__route">
          <ApprovalTrack order={directOrder} variant="full" />
        </div>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          style={{ marginTop: 8 }}
        />
      </Drawer>
    </DOTheme>
  );
};

export default DirectOrderViewModal;
